function toCamelCase(value) {
  return String(value).replace(/[-_]+([a-zA-Z])/g, (_, char) => char.toUpperCase());
}

export function getTablePreferenceTableKey(tableId, variant) {
  const base = toCamelCase(tableId);

  if (!variant) {
    return base;
  }

  const variantSuffix = String(variant).charAt(0).toUpperCase() + String(variant).slice(1);

  return `${base}${variantSuffix}`;
}

export function normalizePreferenceSettings(settings) {
  if (!settings || Array.isArray(settings)) {
    return {};
  }

  return settings;
}

export function normalizeTablePreferences(tablePreferences) {
  if (!tablePreferences || Array.isArray(tablePreferences)) {
    return {};
  }

  return tablePreferences;
}

export function normalizeColumnHeaders(headers = []) {
  return headers
    .map((header) => {
      const id = header.id ?? header.value;

      if (id == null || id === "") {
        return null;
      }

      return {
        id,
        title: header.title ?? header.text ?? header.label ?? String(id),
        disabled: Boolean(header.disabled),
      };
    })
    .filter(Boolean);
}

export function getDefaultColumnSettings(headers) {
  const ids = normalizeColumnHeaders(headers).map((header) => header.id);

  return {
    order: [...ids],
    visible: [...ids],
  };
}

export function preferenceToInternal(preference) {
  const columns = preference?.columns;

  if (!Array.isArray(columns) || !columns.length) {
    return null;
  }

  const order = columns.map((column) => column.key).filter(Boolean);
  const visible = columns.filter((column) => column.visible).map((column) => column.key);

  return { order, visible };
}

export function internalToApiColumns({ order, visible }) {
  return order.map((key) => ({
    key,
    visible: visible.includes(key),
  }));
}

export function readColumnSettingsFromPreferences(tablePreferences, tableKey) {
  if (!tableKey) {
    return null;
  }

  const preferences = normalizeTablePreferences(tablePreferences);
  const entry = preferences[tableKey];

  return preferenceToInternal(entry);
}

export function mergeColumnSettings(headers, stored) {
  const defaults = getDefaultColumnSettings(headers);

  if (!stored) {
    return defaults;
  }

  const headerIds = defaults.order;
  const knownIds = new Set(stored.order);
  const nextOrder = [
    ...stored.order.filter((id) => headerIds.includes(id)),
    ...headerIds.filter((id) => !knownIds.has(id)),
  ];
  const nextVisible = [
    ...stored.visible.filter((id) => headerIds.includes(id)),
    ...headerIds.filter((id) => !knownIds.has(id)),
  ];

  return {
    order: nextOrder,
    visible: nextVisible,
  };
}

export function getAppliedColumnIds(headers, tablePreferences, tableKey) {
  const settings = mergeColumnSettings(
    headers,
    readColumnSettingsFromPreferences(tablePreferences, tableKey)
  );

  return settings.order.filter((id) => settings.visible.includes(id));
}

export function buildTablePreferencePayload(settings, preferenceSettings = {}) {
  return {
    columns: internalToApiColumns(settings),
    settings: normalizePreferenceSettings(preferenceSettings),
  };
}

export function buildResetTablePreferencePayload(preferenceSettings = {}) {
  return {
    columns: [],
    settings: normalizePreferenceSettings(preferenceSettings),
  };
}

export function applyColumnOrder(headers, appliedIds) {
  return appliedIds
    .map((id) => headers.find((header) => (header.id ?? header.value) === id))
    .filter(Boolean);
}
